// Angular imports
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

// RxJs imports
import { Observable, of } from 'rxjs';
import { delay, share, tap } from 'rxjs/operators';

// Model imports
import { CacheEntry } from '../_models/cache-entry';

// Service imports
import { MPCacheRegistrationService } from '../_services/shared-services/mp-cache-registration-service';

@Injectable()
export class UriCachingInterceptor implements HttpInterceptor {
    static MAX_WAIT_TIME = 30000;
    static CHECK_INTERVAL = 1000;

    constructor(private readonly cacheRegistrationService: MPCacheRegistrationService) {}

    public intercept(httpRequest: HttpRequest<any>, handler: HttpHandler) {
        // If not get method, serialize body with custom logic for date ISO format
        if (httpRequest.method !== 'GET') {
            let payload = httpRequest.serializeBody();
            if (typeof payload === 'string') {
                payload = payload.replace(/T00:00:00.000Z/gi, 'T00:00:00.000');
                payload = JSON.parse(payload);
            }
            httpRequest = httpRequest.clone({
                body: payload
            });
        }

        // Don't cache if
        // 1. It's not a GET request
        // 2. If URI is not supposed to be cached
        if (httpRequest.method !== 'GET' || !this.cacheRegistrationService.isNeedToCache(httpRequest.url)) {
            return handler.handle(httpRequest);
        }

        // Also leave scope of resetting already cached data for a URI
        if (httpRequest.headers.get('reset-cache')) {
            this.cacheRegistrationService.deleteCache(httpRequest.urlWithParams);
        }

        return new Observable<HttpEvent<any>>((observer) => {
            let waitTime = 0;
            let timerId = null;
            // Checked if there is cached data for this URI
            let cacheEntry = this.cacheRegistrationService.getCacheEntry(httpRequest.urlWithParams);
            if (!!cacheEntry) {
                if (!!cacheEntry.response) {
                    // If the request is already cached
                    // then let send cached response with minimal delay for UI to repaint correctly
                    of(cacheEntry.response.clone()).pipe(delay(10)).subscribe(observer);
                } else {
                    // If the same request of going through for multiple times
                    // then let wait for 30 sec to get the response from first request before proceed and cache the response
                    timerId = setInterval(() => {
                        waitTime += UriCachingInterceptor.CHECK_INTERVAL;
                        if (!!cacheEntry.response) {
                            clearInterval(timerId);
                            of(cacheEntry.response.clone()).subscribe(observer);
                        } else if (waitTime > UriCachingInterceptor.MAX_WAIT_TIME) {
                            clearInterval(timerId);
                            this.getHttpEvent(httpRequest, handler).subscribe(observer);
                        }
                    }, UriCachingInterceptor.CHECK_INTERVAL);
                }
            } else {
                // If the request of going through for first time
                // then set isLoading to true and proceed with request
                cacheEntry = new CacheEntry();
                cacheEntry.isLoading = true;
                this.cacheRegistrationService.addToCache(httpRequest.urlWithParams, cacheEntry);
                this.getHttpEvent(httpRequest, handler).subscribe(observer);
            }
        });
    }

    private getHttpEvent(httpRequest: HttpRequest<any>, handler: HttpHandler): Observable<HttpEvent<any>> {
        return handler.handle(httpRequest).pipe(
            tap((stateEvent) => {
                if (stateEvent instanceof HttpResponse && stateEvent.ok) {
                    const cacheEntry = this.cacheRegistrationService.getCacheEntry(httpRequest.urlWithParams);
                    if (!!cacheEntry) {
                        cacheEntry.response = stateEvent.clone();
                        cacheEntry.lastRead = Date.now();
                        cacheEntry.isLoading = false;
                    }
                }
            }),
            share()
        );
    }
}
